import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { axiosInstance } from '../api/config/axiosConfig';
import { favoritesService } from '../api/services/favoritesService';
import CreditsCard from '../components/CreditsCard';

interface Stats {
  ads: number;
  favorites: number;
  conversations: number;
}

export default function Overview() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [stats, setStats] = useState<Stats>({ ads: 0, favorites: 0, conversations: 0 });
  const [recentListings, setRecentListings] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const stored = localStorage.getItem('userData');
    if (stored) setUser(JSON.parse(stored));
  }, []);

  useEffect(() => {
    Promise.allSettled([
      axiosInstance.get('/listings/my-listings', { params: { page: 1, limit: 3 } }),
      favoritesService.getFavorites(1, 1),
      axiosInstance.get('/chat/conversations'),
    ]).then(([adsRes, favsRes, convRes]) => {
      setStats({
        ads:
          adsRes.status === 'fulfilled'
            ? adsRes.value.data?.pagination?.total ?? adsRes.value.data?.total ?? 0
            : 0,
        favorites:
          favsRes.status === 'fulfilled' ? favsRes.value.pagination.total : 0,
        conversations:
          convRes.status === 'fulfilled'
            ? convRes.value.data?.conversations?.length ??
              convRes.value.data?.length ??
              0
            : 0,
      });

      if (adsRes.status === 'fulfilled') {
        const responseData = adsRes.value.data;
        const listingsArray = Array.isArray(responseData) ? responseData : (responseData?.listings || responseData?.data || []);
        
        setStats(prev => ({
          ...prev,
          ads: Array.isArray(responseData) ? responseData.length : (responseData?.pagination?.total ?? responseData?.total ?? responseData?.count ?? 0)
        }));
        
        setRecentListings(listingsArray.slice(0, 4));
      }
      setLoading(false);
    });
  }, []);

  const firstName = user?.firstName || 'there';

  return (
    <div className="dashboard-overview" style={{ paddingBottom: 40 }}>
      <div className="d-flex justify-content-between align-items-end mb-4">
        <div>
          <h2 className="fw-bold mb-1">{t('overview.greeting', { name: firstName })}</h2>
          <p className="text-muted mb-0">{t('overview.subtitle', 'Welcome back to your dashboard')}</p>
        </div>
        <div className="d-flex gap-3">
          <button className="btn btn-light shadow-sm" onClick={() => navigate('/dashboard/credits')}>
            {t('dashboard.buy_credits_btn')}
          </button>
          <button className="btn btn-primary shadow-sm" style={{ background: '#fe5a1d', border: 'none' }} onClick={() => navigate('/dashboard/add-user-listing')}>
            <i className="bi bi-plus-circle me-2"></i> {t('dashboard.create_listing')}
          </button>
        </div>
      </div>

      {/* KPI Stats Grid */}
      <div className="row g-4 mb-4">
        <div className="col-md-4">
          <div className="dash-stat-card shadow-sm">
            <div className="dash-stat-icon" style={{ background: 'rgba(254,90,29,0.1)', color: '#fe5a1d' }}>
              <i className="bi bi-tags"></i>
            </div>
            <div className="dash-stat-info">
              <p className="text-muted mb-1 text-uppercase fw-bold" style={{ fontSize: 11, letterSpacing: 0.5 }}>{t('dashboard.active_listings')}</p>
              <h3 className="fw-bold mb-0">
                {loading ? <span className="spinner-border spinner-border-sm text-primary"></span> : stats.ads}
              </h3>
            </div>
          </div>
        </div>
        
        <div className="col-md-4">
          <div className="dash-stat-card shadow-sm">
            <div className="dash-stat-icon" style={{ background: 'rgba(40,167,69,0.1)', color: '#28a745' }}>
              <i className="bi bi-chat-dots"></i>
            </div>
            <div className="dash-stat-info">
              <p className="text-muted mb-1 text-uppercase fw-bold" style={{ fontSize: 11, letterSpacing: 0.5 }}>{t('dashboard.conversations')}</p>
              <h3 className="fw-bold mb-0">
                {loading ? <span className="spinner-border spinner-border-sm text-success"></span> : stats.conversations}
              </h3>
            </div>
          </div>
        </div>
        
        <div className="col-md-4">
          <div className="dash-stat-card shadow-sm">
            <div className="dash-stat-icon" style={{ background: 'rgba(23,162,184,0.1)', color: '#17a2b8' }}>
              <i className="bi bi-heart"></i>
            </div>
            <div className="dash-stat-info">
              <p className="text-muted mb-1 text-uppercase fw-bold" style={{ fontSize: 11, letterSpacing: 0.5 }}>{t('dashboard.saved_vehicles')}</p>
              <h3 className="fw-bold mb-0">
                {loading ? <span className="spinner-border spinner-border-sm text-info"></span> : stats.favorites}
              </h3>
            </div>
          </div>
        </div>
      </div>

      <div className="row g-4">
        {/* Main Content Area */}
        <div className="col-lg-8">
          <div className="dash-panel shadow-sm mb-4">
            <div className="dash-panel-header d-flex justify-content-between align-items-center mb-3">
              <h5 className="fw-bold mb-0">{t('dashboard.recent_listings')}</h5>
              <Link to="/dashboard/ads" className="btn btn-sm btn-link text-decoration-none">{t('dashboard.view_all')}</Link>
            </div>
            
            <div className="dash-panel-body">
              {loading ? (
                <div className="d-flex justify-content-center p-5">
                  <span className="spinner-border text-muted"></span>
                </div>
              ) : recentListings.length > 0 ? (
                <div className="list-group list-group-flush mt-2">
                  {recentListings.map(listing => {
                    const imageUrl = listing.thumbnailUrl || listing.image || '/images/placeholder.jpg';

                    return (
                      <div key={listing.id} className="list-group-item d-flex justify-content-between align-items-center py-3 px-0 border-bottom border-light">
                        <div className="d-flex align-items-center gap-3 w-100">
                          <div style={{ width: 80, height: 60, borderRadius: '8px', overflow: 'hidden', background: '#f1f5f9', border: '1px solid #e2e8f0', flexShrink: 0 }}>
                            <img
                              src={imageUrl}
                              alt="car"
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              onError={(e) => { (e.target as HTMLImageElement).src = '/images/placeholder.jpg'; }}
                            />
                          </div>
                          <div className="flex-grow-1" style={{ minWidth: 0 }}>
                            <Link to={`/listing/${listing.slug || listing.id}`} className="text-decoration-none text-dark fw-bold text-truncate d-block mb-1" style={{ fontSize: '15px' }}>
                              {listing.title || `${listing.year ?? ''} ${listing.make ?? ''} ${listing.model ?? ''}`.trim() || 'Untitled Listing'}
                            </Link>
                            <div className="d-flex align-items-center gap-3">
                              <span className="text-success fw-bold" style={{ fontSize: '13px' }}>
                                {listing.price ? `${Number(listing.price).toLocaleString('de-DE')} ALL` : 'Price on request'}
                              </span>
                              <span className="text-muted" style={{ fontSize: '12px' }}>
                                <i className="bi bi-speedometer2 me-1"></i>
                                {listing.mileage ? `${listing.mileage.toLocaleString('de-DE')} km` : '0 km'}
                              </span>
                            </div>
                          </div>
                          <div className="d-flex flex-column align-items-end gap-2 flex-shrink-0 ms-3">
                            <span className={`badge rounded-pill px-3 py-1 fw-semibold ${listing.status === 'LIVE' ? 'bg-success bg-opacity-10 text-success' : listing.status === 'PENDING' ? 'bg-warning bg-opacity-10 text-warning text-uppercase' : 'bg-secondary bg-opacity-10 text-secondary'}`}>
                              {listing.status || 'DRAFT'}
                            </span>
                            <span className="text-muted" style={{ fontSize: '11px' }}>
                              <i className="bi bi-eye me-1"></i> {listing.viewCount || 0} views
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center p-4">
                  <div className="text-muted mb-3"><i className="bi bi-car-front fs-1"></i></div>
                  <h6 className="fw-semibold">{t('overview_sections.no_recent_title')}</h6>
                  <p className="text-muted small">{t('overview_sections.no_recent_desc')}</p>
                  <Link to="/dashboard/add-user-listing" className="btn btn-outline-primary btn-sm mt-2">{t('overview_sections.create_first_ad')}</Link>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar panels */}
        <div className="col-lg-4">
          <div className="mb-4">
            <CreditsCard />
          </div>

          <div 
            className="dash-panel shadow-sm p-4 position-relative overflow-hidden" 
            style={{ 
              background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', 
              color: '#fff',
              border: 'none',
              borderRadius: '16px'
            }}
          >
             {/* Decorative Background Icon */}
             <i className="bi bi-rocket-takeoff position-absolute" style={{ fontSize: '120px', color: 'rgba(254,255,255,0.03)', right: '-20px', bottom: '-20px', transform: 'rotate(-15deg)' }}></i>
             
             {/* Content */}
             <div className="position-relative z-1">
               <div className="d-flex align-items-center mb-3">
                 <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ width: '48px', height: '48px', background: 'rgba(254, 90, 29, 0.15)', color: '#fe5a1d' }}>
                   <i className="bi bi-lightning-charge-fill fs-4"></i>
                 </div>
                 <h5 className="fw-bold mb-0">{t('overview_sections.boost_title')}</h5>
               </div>

               <p className="small mb-4" style={{ color: '#94a3b8', lineHeight: '1.6' }}>
                 {t('overview_sections.boost_desc')}
               </p>
               
               <Link 
                 to="/dashboard/credits" 
                 className="btn w-100 fw-bold shadow-sm"
                 style={{ 
                   background: '#fe5a1d', 
                   color: '#fff', 
                   border: 'none', 
                   borderRadius: '10px',
                   padding: '10px',
                   transition: 'all 0.2s ease-in-out'
                 }}
                 onMouseEnter={(e) => {
                   e.currentTarget.style.transform = 'translateY(-2px)';
                   e.currentTarget.style.boxShadow = '0 6px 16px rgba(254, 90, 29, 0.3)';
                 }}
                 onMouseLeave={(e) => {
                   e.currentTarget.style.transform = 'translateY(0)';
                   e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.05)';
                 }}
               >
                 {t('overview_sections.buy_promotion')}
               </Link>
             </div>
          </div>
        </div>
      </div>

    </div>
  );
}
